import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMemberAccessToken, supabase } from '../auth/supabase';
import useTrackingStore from '../tracking/useTrackingStore';
import { personNamesMatch } from '../tracking/deploymentUsageModel';
import {
  attachmentSizeLabel,
  MAX_PROGRAM_FEED_ATTACHMENTS,
  PROGRAM_FEED_ATTACHMENT_ACCEPT,
  uploadProgramFeedAttachment,
  validateProgramFeedFile,
} from '../program/programFeedAttachments';
import './admin.css';
import './v12-admin.css';

const REMS_SHARED_EMAIL = '24rem@metrofire.ca.gov';
const EMPTY = {
  title: '',
  body: '',
  priority: 'normal',
  audience: 'all_line_medics',
  commentsEnabled: false,
  acknowledgmentRequired: false,
  pushRequested: false,
  smsRequested: false,
};
const EMPTY_DEPLOYMENT_OPPORTUNITY = {
  rank: '',
  position: 'EMPF',
  contactName: '',
  contactPhone: '',
};

function activeLineMedic(member) {
  return member?.approved && !member?.disabled && String(member?.email || '').trim().toLowerCase() !== REMS_SHARED_EMAIL;
}

function when(value) {
  if (!value) return 'Draft';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function displayAudience(value) {
  return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

async function dispatchQueued(limit) {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  const response = await fetch('/api/notifications', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'dispatch_queued', limit: Math.max(1, Math.min(100, Number(limit) || 40)) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to dispatch queued notifications.');
  return payload;
}

export default function ProgramFeedAdmin({ profile }) {
  const { state } = useTrackingStore(profile?.id);
  const [form, setForm] = useState(EMPTY);
  const [deploymentOpportunity, setDeploymentOpportunity] = useState(EMPTY_DEPLOYMENT_OPPORTUNITY);
  const [editingId, setEditingId] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [targets, setTargets] = useState([]);
  const [acks, setAcks] = useState([]);
  const [reads, setReads] = useState([]);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removeAttachmentIds, setRemoveAttachmentIds] = useState([]);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [warning, setWarning] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.is_admin) return;
    setError('');
    const [postResult, targetResult, ackResult, readResult, commentResult, attachmentResult, memberResult] = await Promise.all([
      supabase.from('program_feed_posts').select('*').order('created_at', { ascending: false }),
      supabase.from('program_feed_targets').select('post_id, member_id, created_at'),
      supabase.from('program_feed_acknowledgments').select('post_id, member_id, revision, acknowledged_at'),
      supabase.from('program_feed_reads').select('post_id, member_id, revision, read_at'),
      supabase.from('program_feed_comments').select('id, post_id, member_id, body, created_at, deleted_at, deleted_by').order('created_at', { ascending: true }),
      supabase.from('program_feed_attachments').select('*').order('created_at', { ascending: true }),
      supabase.rpc('admin_list_notification_members'),
    ]);
    const firstError = postResult.error || targetResult.error || ackResult.error || readResult.error || commentResult.error || attachmentResult.error || memberResult.error;
    if (firstError) {
      setError(firstError.message || 'Unable to load Program Feed administration.');
      setStatus('error');
      return;
    }
    setPosts(Array.isArray(postResult.data) ? postResult.data : []);
    setTargets(Array.isArray(targetResult.data) ? targetResult.data : []);
    setAcks(Array.isArray(ackResult.data) ? ackResult.data : []);
    setReads(Array.isArray(readResult.data) ? readResult.data : []);
    setComments(Array.isArray(commentResult.data) ? commentResult.data : []);
    setAttachments(Array.isArray(attachmentResult.data) ? attachmentResult.data : []);
    setMembers((Array.isArray(memberResult.data) ? memberResult.data : []).filter(activeLineMedic));
    setStatus('ready');
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const deployedMembers = useMemo(() => {
    const activeNames = (state?.deployments || []).filter(item => item.status !== 'closed').map(item => item.person).filter(Boolean);
    return members.filter(member => activeNames.some(name => personNamesMatch(member.full_name, name)));
  }, [members, state?.deployments]);

  const targetMembers = useMemo(() => {
    if (form.audience === 'specific_members') return members.filter(member => selectedMembers.includes(member.id));
    if (form.audience === 'currently_deployed') return deployedMembers;
    return members;
  }, [form.audience, members, selectedMembers, deployedMembers]);

  const memberById = useMemo(() => new Map(members.map(member => [member.id, member])), [members]);
  const toggleMember = id => setSelectedMembers(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);

  const resetEditor = () => {
    setForm(EMPTY);
    setSelectedMembers([]);
    setPendingFiles([]);
    setRemoveAttachmentIds([]);
    setEditingId('');
  };

  const applyDeploymentOpportunity = () => {
    const rank = deploymentOpportunity.rank.trim();
    const position = deploymentOpportunity.position.trim();
    const contactName = deploymentOpportunity.contactName.trim();
    const contactPhone = deploymentOpportunity.contactPhone.trim();
    setError('');
    setWarning('');
    setMessage('');
    if (!rank || !position || !contactName || !contactPhone) {
      setError('Complete rank, EMPF/EMTF, contact name, and contact phone before loading the deployment opportunity template.');
      return;
    }
    setEditingId('');
    setSelectedMembers([]);
    setForm({
      title: `Deployment Opportunity — ${rank} ${position}`,
      body: `Deployment opportunity for ${rank} ${position}. Contact ${contactName} at ${contactPhone} if available.`,
      priority: 'important',
      audience: 'all_line_medics',
      commentsEnabled: false,
      acknowledgmentRequired: false,
      pushRequested: true,
      smsRequested: true,
    });
    setMessage('Deployment opportunity loaded. Review the audience and message, then publish when ready.');
  };

  const startEdit = post => {
    const snapshot = targets.filter(item => item.post_id === post.id).map(item => item.member_id);
    setEditingId(post.id);
    setSelectedMembers(snapshot);
    setPendingFiles([]);
    setRemoveAttachmentIds([]);
    setForm({
      title: post.title || '',
      body: post.body || '',
      priority: post.priority || 'normal',
      audience: post.audience || 'all_line_medics',
      commentsEnabled: Boolean(post.comments_enabled),
      acknowledgmentRequired: Boolean(post.acknowledgment_required),
      pushRequested: Boolean(post.push_requested),
      smsRequested: Boolean(post.sms_requested),
    });
    setMessage('');
    setWarning('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const editingAttachments = attachments.filter(item => item.post_id === editingId && !item.removed_revision);

  const chooseAttachments = event => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    setError('');
    if (!selected.length) return;

    for (const file of selected) {
      const validation = validateProgramFeedFile(file);
      if (validation) {
        setError(`${file.name}: ${validation}`);
        return;
      }
    }

    const retained = editingAttachments.filter(item => !removeAttachmentIds.includes(item.id)).length;
    if (retained + pendingFiles.length + selected.length > MAX_PROGRAM_FEED_ATTACHMENTS) {
      setError(`Program Feed posts can have up to ${MAX_PROGRAM_FEED_ATTACHMENTS} attachments.`);
      return;
    }
    setPendingFiles(current => [...current, ...selected]);
  };

  const removePendingFile = index => {
    setPendingFiles(current => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const toggleExistingAttachment = id => {
    setRemoveAttachmentIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };

  const queueAndDispatch = async (post, audienceMembers, eventType) => {
    const memberIds = audienceMembers.map(member => member.id);
    if ((!form.pushRequested && !form.smsRequested) || !memberIds.length) {
      return { queued: 0, smsEligible: 0, smsSkipped: form.smsRequested ? memberIds.length : 0, dispatchResult: null };
    }

    // Queue creation happens server-side so the target snapshot and audit rows are not
    // dependent on a browser/client INSERT succeeding. This is the acceptance-test fix
    // for Program Feed posts that saved with push_requested=true but produced no APNs rows.
    const { data, error: queueError } = await supabase.rpc('admin_queue_operational_notifications', {
      target_source_type: 'program_feed',
      target_source_id: post.id,
      target_event_type: eventType,
      target_member_ids: memberIds,
      include_push: form.pushRequested,
      include_sms: form.smsRequested,
      target_deep_link: `/program-feed/${post.id}`,
      target_metadata: { post_revision: post.revision },
      target_stamp: `r${post.revision}`,
    });
    if (queueError) throw queueError;

    const queued = Number(data?.queued || 0);
    const dispatchResult = queued > 0
      ? await dispatchQueued(Math.min(100, Math.max(queued + 10, 30)))
      : null;

    return {
      queued,
      smsEligible: Number(data?.sms_eligible || 0),
      smsSkipped: Number(data?.sms_skipped || 0),
      dispatchResult,
    };
  };

  const save = async event => {
    event.preventDefault();
    setError('');
    setMessage('');
    setWarning('');
    if (!form.title.trim() || !form.body.trim()) return setError('Enter a title and message.');
    if (form.audience !== 'all_line_medics' && !targetMembers.length) return setError('This audience currently contains no Line Medic members.');
    const retainedAttachmentCount = editingId
      ? editingAttachments.filter(item => !removeAttachmentIds.includes(item.id)).length
      : 0;
    if (retainedAttachmentCount + pendingFiles.length > MAX_PROGRAM_FEED_ATTACHMENTS) {
      return setError(`Program Feed posts can have up to ${MAX_PROGRAM_FEED_ATTACHMENTS} attachments.`);
    }

    setSaving(true);
    let savedPost = null;
    let notificationWarning = '';
    let attachmentWarning = '';
    try {
      if (editingId) {
        const existing = posts.find(item => item.id === editingId);
        if (!existing) throw new Error('The Program Feed post is no longer available.');
        const { data: updated, error: updateError } = await supabase.from('program_feed_posts').update({
          title: form.title.trim(),
          body: form.body.trim(),
          priority: form.priority,
          audience: form.audience,
          comments_enabled: form.commentsEnabled,
          acknowledgment_required: form.acknowledgmentRequired,
          push_requested: form.pushRequested,
          sms_requested: form.smsRequested,
          revision: Number(existing.revision || 1) + 1,
          updated_at: new Date().toISOString(),
        }).eq('id', existing.id).select('*').single();
        if (updateError) throw updateError;
        savedPost = updated;

        const { error: deleteTargetError } = await supabase.from('program_feed_targets').delete().eq('post_id', existing.id);
        if (deleteTargetError) throw deleteTargetError;
      } else {
        const { data: inserted, error: postError } = await supabase.from('program_feed_posts').insert({
          title: form.title.trim(),
          body: form.body.trim(),
          author_user_id: profile.id,
          priority: form.priority,
          audience: form.audience,
          comments_enabled: form.commentsEnabled,
          acknowledgment_required: form.acknowledgmentRequired,
          push_requested: form.pushRequested,
          sms_requested: form.smsRequested,
          published_at: new Date().toISOString(),
        }).select('*').single();
        if (postError) throw postError;
        savedPost = inserted;
      }

      if (targetMembers.length) {
        const { error: targetError } = await supabase.from('program_feed_targets').insert(targetMembers.map(member => ({ post_id: savedPost.id, member_id: member.id })));
        if (targetError) throw targetError;
      }

      if (editingId && removeAttachmentIds.length) {
        const { error: removeError } = await supabase
          .from('program_feed_attachments')
          .update({
            removed_revision: savedPost.revision,
            removed_at: new Date().toISOString(),
            removed_by: profile.id,
          })
          .in('id', removeAttachmentIds)
          .eq('post_id', savedPost.id)
          .is('removed_revision', null);
        if (removeError) attachmentWarning = `Some existing attachments could not be removed: ${removeError.message}`;
      }

      if (pendingFiles.length) {
        const failedAttachments = [];
        for (const file of pendingFiles) {
          try {
            await uploadProgramFeedAttachment(file, savedPost);
          } catch (attachmentError) {
            failedAttachments.push(`${file.name}: ${attachmentError?.message || 'upload failed'}`);
          }
        }
        if (failedAttachments.length) {
          attachmentWarning = `${attachmentWarning ? `${attachmentWarning} ` : ''}${failedAttachments.length} attachment${failedAttachments.length === 1 ? '' : 's'} need attention: ${failedAttachments.join(' · ')}`;
        }
      }

      try {
        const delivery = await queueAndDispatch(savedPost, targetMembers, editingId ? 'program_feed_revised' : 'program_feed_published');
        if (delivery.smsSkipped > 0) {
          notificationWarning = `${delivery.smsSkipped} targeted member${delivery.smsSkipped === 1 ? ' was' : 's were'} not eligible for optional operational SMS and were not texted.`;
        }
        const failed = Number(delivery.dispatchResult?.failed || 0);
        if (failed > 0) notificationWarning = `${notificationWarning ? `${notificationWarning} ` : ''}${failed} provider send${failed === 1 ? '' : 's'} failed; the post is saved and the failures are preserved in Notification Audit.`;
      } catch (notificationError) {
        notificationWarning = `The post was saved, but notification processing needs attention: ${notificationError?.message || 'notification dispatch failed.'}`;
      }

      setMessage(editingId
        ? `Revision ${savedPost.revision} saved for ${targetMembers.length} Line Medic${targetMembers.length === 1 ? '' : 's'}. Any prior acknowledgment remains tied to the earlier revision.`
        : `Published to ${targetMembers.length} Line Medic${targetMembers.length === 1 ? '' : 's'}.`);
      setWarning([attachmentWarning, notificationWarning].filter(Boolean).join(' '));
      resetEditor();
      await load();
    } catch (saveError) {
      setError(saveError?.message || 'Unable to save the Program Feed post.');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const archive = async post => {
    if (!window.confirm(`Archive “${post.title}”?`)) return;
    const { error: updateError } = await supabase.from('program_feed_posts').update({ archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('id', post.id);
    if (updateError) return setError(updateError.message || 'Unable to archive this post.');
    await load();
  };

  const moderateComment = async comment => {
    if (comment.deleted_at || !window.confirm('Remove this Program Feed comment?')) return;
    const { error: updateError } = await supabase.from('program_feed_comments').update({ deleted_at: new Date().toISOString(), deleted_by: profile.id }).eq('id', comment.id);
    if (updateError) return setError(updateError.message || 'Unable to remove this comment.');
    await load();
  };

  if (!profile?.is_admin) return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1></section></main></div>;

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div><h1>Program Feed</h1><p>Create targeted Line Medic announcements, acknowledgments, comments, push alerts, and optional operational SMS</p></div>
        <div className="admin-topbar-actions"><button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button><button type="button" onClick={() => window.location.assign('/program-feed')}>Member View</button></div>
      </header>
      <main className="admin-page">
        <section className="admin-card">
          <div className="admin-section-head"><div><h2>{editingId ? 'Edit Program Feed Post' : 'New Program Feed Post'}</h2><p>REMS is excluded from Program Feed by design and database policy. Provider failures never delete the in-app post.</p></div>{editingId && <button type="button" onClick={resetEditor}>Cancel Edit</button>}</div>
          {!editingId && <div className="v12-admin-note" style={{ marginBottom: 14 }}>
            <strong>Deployment Opportunity quick template</strong>
            <div className="v12-admin-grid" style={{ marginTop: 10 }}>
              <label>Rank<input value={deploymentOpportunity.rank} placeholder="Firefighter, Engineer, Captain…" onChange={event => setDeploymentOpportunity(value => ({ ...value, rank: event.target.value }))} /></label>
              <label>Position<select value={deploymentOpportunity.position} onChange={event => setDeploymentOpportunity(value => ({ ...value, position: event.target.value }))}><option value="EMPF">EMPF</option><option value="EMTF">EMTF</option></select></label>
              <label>Contact name<input value={deploymentOpportunity.contactName} placeholder="Admin / program contact" onChange={event => setDeploymentOpportunity(value => ({ ...value, contactName: event.target.value }))} /></label>
              <label>Contact phone<input type="tel" value={deploymentOpportunity.contactPhone} placeholder="(916) 555-0123" onChange={event => setDeploymentOpportunity(value => ({ ...value, contactPhone: event.target.value }))} /></label>
            </div>
            <button type="button" onClick={applyDeploymentOpportunity} style={{ marginTop: 10 }}>Load Deployment Opportunity</button>
          </div>}
          <form className="v12-admin-form" onSubmit={save}>
            <label>Title<input value={form.title} maxLength={160} onChange={event => setForm(value => ({ ...value, title: event.target.value }))} required /></label>
            <label>Message<textarea value={form.body} maxLength={12000} onChange={event => setForm(value => ({ ...value, body: event.target.value }))} required /></label>
            <div className="v12-admin-note program-feed-attachment-editor">
              <strong>Attachments</strong>
              <span>Add up to {MAX_PROGRAM_FEED_ATTACHMENTS} photos or PDFs. Each file may be up to 15 MB.</span>
              {editingId && editingAttachments.length > 0 && (
                <div className="program-feed-attachment-admin-list">
                  {editingAttachments.map(item => {
                    const removing = removeAttachmentIds.includes(item.id);
                    return (
                      <button
                        type="button"
                        key={item.id}
                        className={`program-feed-attachment-admin-item${removing ? ' removing' : ''}`}
                        onClick={() => toggleExistingAttachment(item.id)}
                      >
                        <span>{item.mime_type === 'application/pdf' ? 'PDF' : 'IMAGE'}</span>
                        <strong>{item.original_name}</strong>
                        <small>{attachmentSizeLabel(item.size_bytes)} · {removing ? 'Will be removed from this revision' : 'Tap to remove'}</small>
                      </button>
                    );
                  })}
                </div>
              )}
              {pendingFiles.length > 0 && (
                <div className="program-feed-attachment-admin-list">
                  {pendingFiles.map((file, index) => (
                    <button type="button" key={`${file.name}-${file.size}-${index}`} className="program-feed-attachment-admin-item pending" onClick={() => removePendingFile(index)}>
                      <span>NEW</span>
                      <strong>{file.name}</strong>
                      <small>{attachmentSizeLabel(file.size)} · Tap to remove</small>
                    </button>
                  ))}
                </div>
              )}
              <label className="program-feed-attachment-picker">
                <input type="file" multiple accept={PROGRAM_FEED_ATTACHMENT_ACCEPT} onChange={chooseAttachments} />
                <span>+ Add Photo or PDF</span>
              </label>
            </div>
            <div className="v12-admin-grid">
              <label>Priority<select value={form.priority} onChange={event => setForm(value => ({ ...value, priority: event.target.value }))}><option value="normal">Normal</option><option value="important">Important</option><option value="urgent">Urgent</option></select></label>
              <label>Audience<select value={form.audience} onChange={event => setForm(value => ({ ...value, audience: event.target.value }))}><option value="all_line_medics">All Line Medics</option><option value="specific_members">Specific Members</option><option value="currently_deployed">Currently Deployed</option></select></label>
            </div>
            {form.audience === 'specific_members' && <div className="v12-member-picker">{members.map(member => <label key={member.id}><input type="checkbox" checked={selectedMembers.includes(member.id)} onChange={() => toggleMember(member.id)} />{member.full_name || member.email}</label>)}</div>}
            {form.audience === 'currently_deployed' && <div className="v12-admin-note">Currently resolves to {deployedMembers.length} member{deployedMembers.length === 1 ? '' : 's'} from active deployment data. Saving snapshots that audience for reporting.</div>}
            <div className="v12-check-row">
              <label><input type="checkbox" checked={form.commentsEnabled} onChange={event => setForm(value => ({ ...value, commentsEnabled: event.target.checked }))} /> Allow comments</label>
              <label><input type="checkbox" checked={form.acknowledgmentRequired} onChange={event => setForm(value => ({ ...value, acknowledgmentRequired: event.target.checked }))} /> Require acknowledgment</label>
              <label><input type="checkbox" checked={form.pushRequested} onChange={event => setForm(value => ({ ...value, pushRequested: event.target.checked }))} /> Push + In-App</label>
              <label><input type="checkbox" checked={form.smsRequested} onChange={event => setForm(value => ({ ...value, smsRequested: event.target.checked }))} /> Optional Operational SMS</label>
            </div>
            <div className="v12-admin-note">Target: {targetMembers.length} Line Medic{targetMembers.length === 1 ? '' : 's'}. SMS only targets members who explicitly opted in and have a verified mobile number. Verification/security SMS is separate.</div>
            <button type="submit" className="v12-primary" disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save New Revision' : 'Publish'}</button>
          </form>
          {message && <div className="v12-success">{message}</div>}
          {warning && <div className="v12-admin-note" style={{ marginTop: 10 }}>{warning}</div>}
          {error && <div className="v12-error">{error}</div>}
        </section>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Published Posts</h2><p>Read and acknowledgment state is tracked per member and per revision. Audience rows are snapshot at publish/edit time for reporting.</p></div><button type="button" onClick={load}>Refresh</button></div>
          {status === 'loading' && <div className="admin-empty">Loading…</div>}
          {posts.map(post => {
            const postTargets = targets.filter(item => item.post_id === post.id).map(item => item.member_id);
            const targetIds = postTargets.length ? postTargets : (post.audience === 'all_line_medics' ? members.map(member => member.id) : []);
            const postAcks = acks.filter(item => item.post_id === post.id && Number(item.revision) === Number(post.revision));
            const postReads = reads.filter(item => item.post_id === post.id && Number(item.revision) === Number(post.revision));
            const acked = new Set(postAcks.map(item => item.member_id));
            const read = new Set(postReads.map(item => item.member_id));
            const outstanding = targetIds.filter(id => !acked.has(id)).map(id => memberById.get(id)?.full_name || memberById.get(id)?.email || id);
            const postComments = comments.filter(item => item.post_id === post.id);
            const postAttachments = attachments.filter(item => item.post_id === post.id && !item.removed_revision);
            return (
              <div className="v12-admin-list-row" key={post.id} style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <strong>{post.title}</strong>
                  <small>{when(post.published_at || post.created_at)} · {displayAudience(post.audience)} · Revision {post.revision}</small>
                  <span>{targetIds.length} targeted · {read.size} read{post.acknowledgment_required ? ` · ${acked.size} acknowledged` : ''}{post.comments_enabled ? ` · ${postComments.filter(item => !item.deleted_at).length} comment(s)` : ''}{postAttachments.length ? ` · ${postAttachments.length} attachment(s)` : ''}{post.push_requested ? ' · push' : ''}{post.sms_requested ? ' · optional SMS' : ''}</span>
                  {post.acknowledgment_required && outstanding.length > 0 && <small style={{ marginTop: 5 }}>Outstanding: {outstanding.join(', ')}</small>}
                  {postComments.length > 0 && <div style={{ marginTop: 10, display: 'grid', gap: 7 }}>{postComments.map(comment => { const author = memberById.get(comment.member_id); return <div key={comment.id} className="v12-admin-note"><strong>{author?.full_name || author?.email || 'Member'}</strong> · {when(comment.created_at)}<div>{comment.deleted_at ? <em>Comment removed by Program Admin.</em> : comment.body}</div>{!comment.deleted_at && <button type="button" onClick={() => moderateComment(comment)} style={{ marginTop: 5 }}>Remove Comment</button>}</div>; })}</div>}
                </div>
                <div>{post.archived_at ? <span className="admin-status">Archived</span> : <><button type="button" onClick={() => window.location.assign(`/program-feed/${post.id}`)}>Open</button><button type="button" onClick={() => startEdit(post)}>Edit</button><button type="button" onClick={() => archive(post)}>Archive</button></>}</div>
              </div>
            );
          })}
          {status === 'ready' && !posts.length && <div className="admin-empty">No Program Feed posts yet.</div>}
        </section>
      </main>
    </div>
  );
}
